/**
 * TableView test Adapters to abstract common unit test actions away from the implementation details.
 */
import { render, RenderResult, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { VNode } from 'preact';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { matchTranslationBundle } from '../../utils/UNSAFE_matchTranslationBundle';
import { RootEnvironment, RootEnvironmentProvider } from '../../UNSAFE_Environment';

/**
 * Constant for timeout for longer running TableView tests
 */
const TABLEVIEW_LONG_TIMEOUT = 10000;

/**
 * Helper method to render a TableView test component with the proper environment setup
 */
export const setupTableView = async (TestComponent: VNode) => {
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  return render(
    <RootEnvironmentProvider environment={env}>{TestComponent}</RootEnvironmentProvider>
  );
};

/**
 * Helper function to get all 'grid' role elements within the given rendered component.
 */
export const getInnerGrids = async (tableView: RenderResult) => {
  return tableView.findAllByRole('grid');
};

/**
 * Helper function to get all 'columnheader' role elements within the given rendered component.
 */
export const getColumnHeaders = async (tableView: RenderResult) => {
  return tableView.findAllByRole('columnheader');
};

/**
 * Helper function to get all 'row' role elements within the given rendered component.
 */
export const getRows = async (tableView: RenderResult) => {
  return tableView.findAllByRole('row');
};

/**
 * Helper function to get all data cell elements within the given data row.
 */
export const getDataCells = async (dataRow: Element) => {
  return dataRow.children;
};

/**
 * Helper function to get all footer elements within the given rendered component.
 */
export const getColumnFooters = async (tableView: RenderResult) => {
  const rows = await getRows(tableView);
  if (rows.length > 2) {
    return rows[rows.length - 1].children;
  }
  return [];
};

/**
 * Helper method to find the containing cell element of a given element.
 */
export const findContainingCell = (
  rootElement: HTMLElement,
  contentElement: HTMLElement
): HTMLElement | null => {
  if (contentElement.dataset['ojCellType'] != null) {
    return contentElement;
  }
  const parentElement = contentElement.parentElement;
  if (parentElement === rootElement || parentElement == null) {
    return null;
  }
  return findContainingCell(rootElement, parentElement);
};

/**
 * Helper function to determine if the current environment is a Mac.
 */
export const isMac = () => {
  return getClientHints().platform === 'mac';
};

/**
 * Helper function to verify the values of aria-selected attributes on specific elements.
 */
export const verifyAriaActiveDescendant = async (gridElement: Element, value: string | null) => {
  await waitFor(() => {
    expect(gridElement.getAttribute('aria-activedescendant')).to.equal(value);
  });
};

/**
 * Helper function to verify the values of aria-selected attributes on specific elements.
 */
export const verifyAriaSelected = async (
  selectedElements: Element[],
  unselectedElements: Element[]
) => {
  for (let i = 0; i < selectedElements.length; i++) {
    await waitFor(() => {
      expect(selectedElements[i].getAttribute('aria-selected')).to.equal('true');
    });
  }
  for (let i = 0; i < unselectedElements.length; i++) {
    await waitFor(() => {
      expect(unselectedElements[i].getAttribute('aria-selected')).to.not.equal('true');
    });
  }
};

/**
 * Helper function to verify the width of a Table element.
 */
export const verifyElementWidth = async (element: HTMLElement, width: number) => {
  await waitFor(() => {
    expect(element.offsetWidth).to.equal(width);
  });
};

/**
 * Helper function to verify the width of a Table element.
 */
export const determineBrowserScrollbarWidth = async (tableView: RenderResult) => {
  const grids = await getInnerGrids(tableView);

  const innerTable = grids[0].children[0].children[0] as HTMLElement;
  return grids[0].offsetWidth - innerTable.offsetWidth;
};

/**
 * Helper function to apply a longer timeout for tests
 */
export const enableLongTimeout = () => {
  if (typeof jest === 'undefined') {
    mocha.timeout(TABLEVIEW_LONG_TIMEOUT);
  } else {
    jest.setTimeout(TABLEVIEW_LONG_TIMEOUT);
  }
};
